import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";
import { cn } from "@/lib/utils";

const HomepageFAQ = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  
  // Try to get theme, provide fallback for pages without provider
  let theme = "dark";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not wrapped in provider, use default
  }

  const faqs = [{
    question: "How much does it cost to build an ADU?",
    answer: "The cost of an ADU depends on size, finishes, site access, and whether it's detached or a conversion. UDA ADUs typically range from $150,000 and UP. Use our ADU Cost Calculator to get an instant estimate."
  }, {
    question: "Can I rent out my ADU for income?",
    answer: "Yes. Most of our clients use their ADU for rental income, generating $2,500–$7,000/month depending on city and unit size. We help clients estimate ROI before design begins."
  }, {
    question: "How long does it take to design and build an ADU?",
    answer: "From site analysis to move-in, most projects take 4–6 months. Pre-permitted plans may reduce this timeline. We handle the entire process in-house."
  }, {
    question: "Do you offer custom designs or only pre-designed plans?",
    answer: "We offer both. UDA ADU specializes in custom ADUs tailored to your lot and lifestyle, and also offers pre-permitted models for faster builds."
  }, {
    question: "What cities do you serve?",
    answer: "We specialize in ADUs across the Bay Area including Los Altos, San Jose, Palo Alto, Sunnyvale, Los Altos, Santa Clara, and surrounding areas."
  }, {
    question: "What is the UDA ADU Wizard?",
    answer: "It's our free online tool that lets you input your address, view buildable area, and preview ADU options on your lot instantly — no guesswork required."
  }];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const cardStyles = cn(
    "rounded-2xl overflow-hidden transition-all duration-500",
    theme === "dark"
      ? "bg-[#0a0a0a] shadow-[8px_8px_16px_#000000,-8px_-8px_16px_#141414]"
      : "bg-[#f5f5f5] shadow-[6px_6px_12px_#d8d8d8,-6px_-6px_12px_#fefefe]"
  );

  const buttonHoverStyles = theme === "dark"
    ? "hover:bg-[#141414]"
    : "hover:bg-[#ebebeb]";

  const questionTextColor = cn("transition-colors duration-500", theme === "dark" ? "text-white" : "text-[#1a1a1a]");
  const answerTextColor = cn("transition-colors duration-500", theme === "dark" ? "text-gray-300" : "text-[#4a4a4a]");
  const subtitleColor = cn("transition-colors duration-500", theme === "dark" ? "text-gray-300" : "text-gray-600");

  return <section className="w-full max-w-4xl mx-auto px-4 py-[18px]">
      <div className="text-center mb-12 py-0 my-0">
        <h2 className="md:text-4xl mb-4 text-[#e4683b] text-3xl font-normal">
          Smart Questions We Answer Every Day
        </h2>
        <p className={cn("max-w-2xl mx-auto text-xl", subtitleColor)}>
          Get instant answers to the most common ADU questions from Bay Area homeowners
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {faqs.map((faq, index) => <div key={index} className={cardStyles}>
            <button onClick={() => toggleFAQ(index)} className={cn("w-full px-6 py-4 text-left flex justify-between items-center transition-colors", buttonHoverStyles)}>
              <h3 className={cn("font-medium pr-4 text-base", questionTextColor)}>
                {faq.question}
              </h3>
              {openFAQ === index ? <ChevronUp className="h-5 w-5 text-[#e4683b] flex-shrink-0" /> : <ChevronDown className="h-5 w-5 text-[#e4683b] flex-shrink-0" />}
            </button>
            {openFAQ === index && <div className="px-6 pb-4">
                <p className={cn("leading-relaxed", answerTextColor)}>
                  {faq.answer}
                </p>
              </div>}
          </div>)}
      </div>

      <div className="text-center">
        <Link to="/faq">
          <Button className="bg-[#FF7A00] hover:bg-[#e56b00] text-white py-3 px-8 rounded-xl shadow-lg transition-colors text-lg">
            View All FAQs
          </Button>
        </Link>
      </div>
    </section>;
};
export default HomepageFAQ;
